// src/components/AddressAutocomplete.js
// Componente de autocomplete de endereco usando Google Places API

import React, { useState, useCallback, useRef } from 'react';
import { Form } from 'react-bootstrap';

/**
 * Componente de input com autocomplete de endereco via Google Places
 *
 * @param {Object} props
 * @param {string} props.value - Valor atual do campo
 * @param {function} props.onChange - Callback quando valor muda (recebe string)
 * @param {Object} props.addressService - Google AutocompleteService instance
 * @param {Object} props.nzBounds - LatLngBounds para restringir busca a NZ
 * @param {string} props.placeholder - Placeholder do input
 * @param {string} props.label - Label do campo (opcional)
 * @param {boolean} props.required - Se o campo eh obrigatorio
 * @param {boolean} props.disabled - Se o campo esta desabilitado
 * @param {string} props.id - ID do input (para acessibilidade)
 */
export default function AddressAutocomplete({
  value,
  onChange,
  addressService,
  nzBounds,
  placeholder = 'Digite o endereço...',
  label,
  required = false,
  disabled = false,
  id = 'address-autocomplete'
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef(null);
  const sessionTokenRef = useRef(null);

  // Cria/renova session token (agrupa chamadas numa cobranca so no Google)
  const getSessionToken = useCallback(() => {
    if (!sessionTokenRef.current && window.google?.maps?.places) {
      sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
    }
    return sessionTokenRef.current;
  }, []);

  // Handler para mudanca no input (com debounce 300ms)
  const handleInputChange = useCallback((e) => {
    const val = e.target.value;
    onChange(val);

    // Limpa timer anterior (debounce)
    if (debounceRef.current) clearTimeout(debounceRef.current);

    // Busca sugestoes se tiver service e valor (com debounce)
    if (addressService && val && val.length >= 3) {
      debounceRef.current = setTimeout(() => {
        addressService.getPlacePredictions(
          {
            input: val,
            componentRestrictions: { country: 'nz' },
            locationRestriction: nzBounds,
            sessionToken: getSessionToken()
          },
          (predictions, status) => {
            if (status === 'OK' && predictions) {
              setSuggestions(predictions);
              setShowSuggestions(true);
            } else {
              setSuggestions([]);
              setShowSuggestions(false);
            }
          }
        );
      }, 300);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [addressService, nzBounds, onChange, getSessionToken]);

  // Handler para selecionar sugestao
  const handleSelectSuggestion = useCallback((description) => {
    onChange(description);
    setSuggestions([]);
    setShowSuggestions(false);
    // Renova session token (sessao completada)
    sessionTokenRef.current = null;
  }, [onChange]);

  // Fecha sugestoes ao clicar fora (blur)
  const handleBlur = useCallback(() => {
    // Delay para permitir click na sugestao antes de fechar
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  }, []);

  // Abre sugestoes ao focar se tiver sugestoes
  const handleFocus = useCallback(() => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  }, [suggestions.length]);

  return (
    <div className="position-relative">
      {label && (
        <Form.Label htmlFor={id}>
          {label}
          {required && <span className="text-danger"> *</span>}
        </Form.Label>
      )}

      <Form.Control
        id={id}
        type="text"
        value={value}
        onChange={handleInputChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        autoComplete="off"
      />

      {/* Dropdown de sugestoes */}
      {showSuggestions && suggestions.length > 0 && (
        <div
          className="list-group position-absolute w-100"
          style={{
            zIndex: 2000,
            maxHeight: '12rem',
            overflowY: 'auto',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
          }}
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion.place_id || index}
              type="button"
              className="list-group-item list-group-item-action"
              onClick={() => handleSelectSuggestion(suggestion.description)}
              style={{ cursor: 'pointer' }}
            >
              {suggestion.description}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Versao simplificada sem label (para uso inline)
 */
export function AddressAutocompleteInput(props) {
  return <AddressAutocomplete {...props} label={null} />;
}
